import every from "lodash/every";
import keys from "lodash/keys";
import { CvExportsButton } from "PFApp/components/cv_exports_button";
import { Action, SubsidiaryModeAlert } from "PFApp/components/subsidiary_mode_alert";
import { Flex } from "PFComponents/containers";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { useProfiles } from "PFCore/hooks/queries/profiles/use_profiles";
import { PermissionRule } from "PFCore/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useProfilesPageContext } from "../../context";
import css from "./bulk_select_overlay.module.scss";
import { DeletePlaceholderProfilesModal } from "./delete_placeholder_profiles_modal";

export const BulkSelectOverlay = () => {
  const { t } = useTranslation(["translation", "profiles"]);
  const { data: currentProfile } = useCurrentProfile();
  const canDeletePlaceholders = useIsPermittedTo(currentProfile)(PermissionRule.DeletePlaceholderProfiles);
  const { selectedIds, setSelectedIds } = useProfilesPageContext();
  const selectedProfileIds = keys(selectedIds);

  const { data: { entries: selectedProfiles } = {}, isFetching } = useProfiles({
    filters: {
      fields: {
        id: selectedProfileIds
      }
    }
  });

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const isEveryProfilePlaceholder = useMemo(
    () => every(selectedProfiles, "profile.placeholder"),
    [selectedProfiles]
  );

  const actions: Action[] = [
    {
      node: (
        <CvExportsButton
          profileIds={selectedProfileIds}
          icon="export"
          kind="ghost"
          text={t("profiles:list.exportCvs", { count: selectedProfileIds.length })}
        />
      )
    },
    {
      icon: "remove",
      onClick: () => setIsDeleteModalVisible(true),
      title: t(
        isEveryProfilePlaceholder
          ? "profiles:list.placeholderProfiles.removePlaceholderProfiles"
          : "profiles:list.placeholderProfiles.removePlaceholderProfilesDisabled"
      ),
      disabled: !canDeletePlaceholders || (selectedProfiles && !isEveryProfilePlaceholder) || isFetching
    }
  ];

  return (
    <Flex center className={css.container}>
      <div className={css.overlay} />
      <SubsidiaryModeAlert
        className={css.alert}
        title={t("translation:countSelected", { count: keys(selectedIds).length })}
        onClose={() => setSelectedIds({})}
        actions={actions}
      />
      {isDeleteModalVisible && (
        <DeletePlaceholderProfilesModal onClose={() => setIsDeleteModalVisible(false)} />
      )}
    </Flex>
  );
};
